<template>
  <ion-modal :is-open="isOpen" @didDismiss="$emit('close')">
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button @click="$emit('back')">
            <ion-icon :icon="arrowBackOutline" />
          </ion-button>
        </ion-buttons>
        <ion-title>Chapter {{ chapterNumber }}: {{ campaign?.name }}</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="$emit('close')">Close</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div class="battles-list">
        <div
          v-for="levelId in campaign?.levelIds"
          :key="levelId"
          :class="[
            'level-card',
            {
              'locked': !isLevelUnlocked(levelId),
              'current': levelId === currentBattle,
              'completed': isLevelComplete(levelId),
            },
          ]"
          @click="onBattleClick(levelId)"
        >
          <div class="level-card-content">
            <span class="level-name">
              <span class="battle-prefix">Battle </span>{{ levelId }}
              <ion-icon v-if="hasVisionViewed(levelId)" :icon="colorWandOutline" class="vision-icon" />
            </span>
            <div v-if="isLevelUnlocked(levelId)" class="level-scores">
              <div :class="['score-line', { 'gold-star': isPlayerScorePerfect(levelId) }]">
                <span class="score-label">{{ isPlayerScorePerfect(levelId) ? 'Perfection!' : 'Your Best:' }}</span>
                <span class="score-value">
                  <ion-icon v-if="isPlayerScorePerfect(levelId)" :icon="star" class="star-icon" />
                  <ion-icon v-if="isPlayerScoreOptimal(levelId)" :icon="starOutline" class="star-icon" />
                  {{ getPlayerBestScore(levelId) }}
                  <span v-if="getPlayerUndoCount(levelId) > 0" class="undo-badge">
                    <span class="undo-count">+{{ getPlayerUndoCount(levelId) }}</span>
                    <ion-icon :icon="arrowUndoOutline" class="undo-icon" />
                  </span>
                </span>
              </div>
              <div v-if="!isPlayerScorePerfect(levelId)" class="score-line best-possible">
                <span class="score-label">Optimal:</span>
                <span class="score-value">{{ getBestPossibleScore(levelId) }}</span>
              </div>
            </div>
            <div v-if="levelId === currentBattle" class="next-badge">NEXT</div>
            <div v-if="!isLevelUnlocked(levelId)">
              <ion-icon :icon="lockClosedOutline" class="lock-icon" />
            </div>
            <div v-else>
              <ion-icon :icon="chevronForwardOutline" class="play-icon" />
            </div>
          </div>
        </div>

        <!-- Reward card -->
        <div
          v-if="campaign?.reward"
          :class="[
            'level-card',
            'reward-card',
            {
              'claimable': isRewardClaimable,
              'claimed': isRewardClaimed,
            },
          ]"
          @click="onRewardClick"
        >
          <div class="reward-card-content">
            <HexBadge v-if="rewardIcon" :icon="rewardIcon" :label="campaign.reward.name" :locked="!isRewardClaimable && !isRewardClaimed" />
            <div class="reward-info">
              <div class="reward-title">
                New Ability: {{ campaign.reward.name }}
              </div>
              <div class="reward-progress">
                {{ completedCount }}/{{ campaign.levelIds.length }} Battles Complete
              </div>
            </div>
            <div class="reward-status">
              <template v-if="isRewardClaimed">
                <ion-icon :icon="checkmarkCircleOutline" class="claimed-icon" />
                <span class="claimed-text">Claimed</span>
              </template>
              <template v-else-if="isRewardClaimable">
                <span class="claim-text">Claim!</span>
              </template>
              <template v-else>
                <ion-icon :icon="lockClosedOutline" class="reward-lock-icon" />
              </template>
            </div>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-modal>
</template>

<script setup>
import { computed, inject } from 'vue';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
} from '@ionic/vue';
import {
  lockClosedOutline,
  star,
  starOutline,
  chevronForwardOutline,
  arrowUndoOutline,
  colorWandOutline,
  arrowBackOutline,
  checkmarkCircleOutline,
} from 'ionicons/icons';
import campaignsData from '../../content/dist/campaigns.json';
import { componentRegistry } from '@/lib/ComponentRegistry';
import HexBadge from './components/HexBadge.vue';

const props = defineProps({
  isOpen: Boolean,
  campaign: Object,
});

const emit = defineEmits(['close', 'back', 'select-level', 'claim-reward']);
const game = inject('game');

// Compute chapter number from campaign index in campaignsData
const chapterNumber = computed(() => {
  if (!props.campaign) return '';
  const index = campaignsData.campaigns.findIndex(c => c.id === props.campaign.id);
  return index >= 0 ? index + 1 : '?';
});

// --- Scoring logic (ported from LevelSelectModal.vue) ---

const isLevelUnlocked = (levelId) => game.state.progress.unlockedLevelIds[levelId];
const getLevelState = (levelId) => game.state.progress.levelStates[levelId] || {};
const hasVisionViewed = (levelId) => game.state.progress.levelStates[levelId]?.visionViewed;
const getOptimalMoveCount = (levelId) => game.state.gameplay.optimalMoveCounts[levelId] ?? null;

const isLevelComplete = (levelId) => !!game.state.progress.completedLevelIds[levelId];

const getPlayerBestScore = (levelId) => {
  const levelState = getLevelState(levelId);
  const perfectMoves = levelState.bestPerfect?.moveCount ?? null;
  const assistedMoves = levelState.bestAssisted?.moveCount ?? null;
  if (perfectMoves === null && assistedMoves === null) return '\u2014';
  let bestMoves;
  if (perfectMoves === null) bestMoves = assistedMoves;
  else if (assistedMoves === null) bestMoves = perfectMoves;
  else bestMoves = assistedMoves < perfectMoves ? assistedMoves : perfectMoves;
  return `${bestMoves}`;
};

const getPlayerUndoCount = (levelId) => {
  const levelState = getLevelState(levelId);
  const perfectMoves = levelState.bestPerfect?.moveCount ?? null;
  const assistedMoves = levelState.bestAssisted?.moveCount ?? null;
  const assistedUndos = levelState.bestAssisted?.undoCount ?? null;
  if (perfectMoves === null && assistedMoves === null) return 0;
  if (perfectMoves === null) return assistedUndos ?? 0;
  if (assistedMoves === null) return 0;
  return assistedMoves < perfectMoves ? (assistedUndos ?? 0) : 0;
};

const getBestPossibleScore = (levelId) => {
  const optimalMoves = getOptimalMoveCount(levelId);
  if (optimalMoves === null) return '?';
  return `${optimalMoves}`;
};

const isPlayerScorePerfect = (levelId) => {
  const levelState = getLevelState(levelId);
  const perfectMoves = levelState.bestPerfect?.moveCount ?? null;
  const assistedMoves = levelState.bestAssisted?.moveCount ?? null;
  const optimalMoves = getOptimalMoveCount(levelId);
  if (perfectMoves === null || optimalMoves === null || perfectMoves !== optimalMoves) return false;
  if (assistedMoves !== null && assistedMoves < perfectMoves) return false;
  return true;
};

const isPlayerScoreOptimal = (levelId) => {
  const levelState = getLevelState(levelId);
  const perfectMoves = levelState.bestPerfect?.moveCount ?? null;
  const assistedMoves = levelState.bestAssisted?.moveCount ?? null;
  const optimalMoves = getOptimalMoveCount(levelId);
  if (assistedMoves !== null && assistedMoves === optimalMoves && (perfectMoves === null || perfectMoves > optimalMoves))
    return true;
  return false;
};

// --- Current battle (first incomplete level in this campaign) ---

const currentBattle = computed(() => {
  if (!props.campaign) return null;
  return props.campaign.levelIds.find(id => !isLevelComplete(id)) ?? null;
});

// --- Reward logic ---

const completedCount = computed(() => {
  if (!props.campaign) return 0;
  return props.campaign.levelIds.filter(isLevelComplete).length;
});

const isAllComplete = computed(() => {
  if (!props.campaign) return false;
  return completedCount.value === props.campaign.levelIds.length;
});

const isRewardClaimed = computed(() => {
  if (!props.campaign?.reward) return false;
  return game.state.progress.claimedRewards[props.campaign.id] === true;
});

const isRewardClaimable = computed(() => {
  return isAllComplete.value && !isRewardClaimed.value;
});

const rewardIcon = computed(() => {
  if (!props.campaign?.reward?.component) return null;
  const ComponentClass = componentRegistry.get(props.campaign.reward.component);
  if (!ComponentClass) return null;
  const instance = new ComponentClass();
  if (typeof instance.getDisplayInfo !== 'function') return null;
  return instance.getDisplayInfo().icon;
});

// --- Event handlers ---

const onBattleClick = (levelId) => {
  if (isLevelUnlocked(levelId)) {
    emit('select-level', levelId);
  }
};

const onRewardClick = () => {
  if (isRewardClaimable.value) {
    emit('claim-reward', props.campaign.id);
  }
};
</script>

<style scoped>
ion-content {
  --background: var(--hw-bg-base);
}

.battles-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

/* --- Battle cards --- */

.level-card {
  background: var(--hw-bg-surface);
  border: 1px solid var(--hw-border);
  border-radius: var(--hw-radius);
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.level-card:hover:not(.locked) {
  background: var(--hw-bg-elevated);
  border-color: var(--hw-border-strong);
  transform: translateY(-2px);
  box-shadow: var(--hw-shadow);
}

.level-card.locked {
  opacity: 0.5;
  cursor: not-allowed;
}

.level-card.completed:not(.current) .level-name,
.level-card.completed:not(.current) .play-icon {
  opacity: 0.75;
}

.level-card.current {
  border-color: var(--hw-accent);
  background: rgba(212, 168, 67, 0.08);
  box-shadow: 0 0 8px var(--hw-accent-glow);
}

.level-card-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.level-name {
  font-size: 1.1em;
  font-weight: bold;
  color: var(--hw-text);
  flex-shrink: 0;
}

.battle-prefix {
  /* Hide on very narrow screens to save space */
}

@media (max-width: 340px) {
  .battle-prefix {
    display: none;
  }
}

.next-badge {
  font-size: 0.7em;
  font-weight: bold;
  color: var(--hw-accent);
  background: rgba(212, 168, 67, 0.12);
  border: 1px solid rgba(212, 168, 67, 0.3);
  border-radius: var(--hw-radius);
  padding: 2px 6px;
  flex-shrink: 0;
}

.play-icon {
  color: var(--hw-text-muted);
  font-size: 24px;
}

.lock-icon {
  color: var(--hw-text-dim);
  font-size: 20px;
}

.level-scores {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
  margin-left: auto;
  min-width: 50%;
}

.score-line {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  align-items: center;
  font-size: 0.9em;
  width: 100%;
}

.score-label {
  color: var(--hw-text-muted);
  text-align: right;
}

.score-value {
  font-weight: bold;
  color: var(--hw-text);
  font-family: monospace;
  text-align: right;
  display: flex;
  align-items: initial;
  gap: 4px;
}

.undo-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  opacity: 0.8;
}

.undo-count {
  font-family: monospace;
}

.undo-icon {
  font-size: 14px;
  vertical-align: middle;
}

.star-icon {
  font-size: 16px;
  vertical-align: middle;
}

.best-possible {
  font-style: italic;
  font-size: 0.85em;
}

.best-possible .score-label {
  color: var(--hw-text-dim);
}

.best-possible .score-value {
  color: var(--hw-text-muted);
}

.gold-star .score-label,
.gold-star .score-value {
  color: var(--hw-gold);
}

.vision-icon {
  font-size: 14px;
  color: var(--hw-vision);
  margin-left: 4px;
  vertical-align: middle;
}

/* --- Reward card --- */

.reward-card {
  border-color: rgba(212, 168, 67, 0.4);
  background: rgba(212, 168, 67, 0.05);
  cursor: default;
}

.reward-card.claimable {
  border-color: var(--hw-gold);
  animation: pulse 2s infinite;
  box-shadow: 0 0 12px var(--hw-accent-glow);
  cursor: pointer;
}

.reward-card.claimable:hover {
  background: rgba(212, 168, 67, 0.12);
  transform: translateY(-2px);
}

.reward-card.claimed {
  opacity: 0.7;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 12px var(--hw-accent-glow);
  }
  50% {
    box-shadow: 0 0 20px rgba(212, 168, 67, 0.5);
  }
}

.reward-card-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.reward-info {
  flex: 1;
}

.reward-title {
  font-size: 1.05em;
  font-weight: bold;
  color: var(--hw-gold);
}

.reward-progress {
  font-size: 0.85em;
  color: var(--hw-text-muted);
  margin-top: 4px;
}

.reward-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.claimed-icon {
  font-size: 24px;
  color: var(--hw-gold);
}

.claimed-text {
  font-size: 0.75em;
  color: var(--hw-gold);
}

.claim-text {
  font-size: 1em;
  font-weight: bold;
  color: var(--hw-gold);
}

.reward-lock-icon {
  font-size: 20px;
  color: rgba(212, 168, 67, 0.4);
}
</style>
